// Continuous ambient bed (Phase 8): underwater drone, calm/tension pads,
// low-health heartbeat, water-rush near currents. All synthesized, all
// long-lived nodes built once on the ambient bus, steered per frame via
// smooth ramps — no per-frame allocations, no clicks.
//
// The game side computes four scalars each frame (systems/ambient.ts) and
// calls steerAmbient(). This module never reads world state.

import { ambientBus, noiseBuffer, whenAudioReady, NOISE_SECONDS } from './audio'
import { T } from '../game/tunables'

export interface AmbientScalars {
  /** 0 shallow → 1 deep. Darkens the drone. */
  biomeFactor: number
  /** Player health fraction 0–1. Heartbeat below the threshold. */
  hpFrac: number
  /** 0 calm → 1 hunted. Crossfades calm pad toward tension pad. */
  tension: number
  /** 0 far → 1 inside a current hazard. Water-rush swell. */
  currentProximity: number
}

interface Nodes {
  ctx: AudioContext
  rumbleFilter: BiquadFilterNode
  rumbleGain: GainNode
  washFilter: BiquadFilterNode
  washGain: GainNode
  rushGain: GainNode
  heartGain: GainNode
}

let nodes: Nodes | null = null
let nextHeartbeat = 0

// Steering time-constant: reaches ~95% of target in 3τ. Fast enough to feel
// responsive, slow enough to never zipper.
const TAU = 0.35

// Where the wash starts inside the shared noise loop, so the two bed layers
// aren't the same sample stream in lockstep. Anything clear of both ends does
// the job; it's a fraction of the loop rather than a number of seconds so that
// shortening the buffer can't push the offset past its end.
const NOISE_PHASE_OFFSET_S = NOISE_SECONDS * 0.46

/** Build the graph once audio is unlocked. Safe to call early; resolves
 *  after the first user gesture. No-op without an AudioContext (tests). */
export async function initAmbientAudio(): Promise<void> {
  await whenAudioReady()
  const ab = ambientBus()
  if (!ab || nodes) return
  const { ctx, bus } = ab

  // ── Low rumble: heavily lowpassed noise ──────────────────────────────────
  //
  // This used to be two sines at 55 and 55.7 Hz, and that pair is what
  // "annoying hum" meant. Two equal-amplitude tones Δf apart don't sound like
  // a thicker tone, they sound like one tone whose amplitude swings:
  //
  //     sin(2πf₁t) + sin(2πf₂t) = 2·cos(π·Δf·t)·sin(2π·f̄·t)
  //
  // With Δf = 0.7 Hz that envelope reaches a full null 0.7 times a second —
  // the bed swelled and cancelled itself **every 1.43 seconds, forever**, at
  // 100% depth, and at the same rate in both biomes (`droneHz` moved both
  // oscillators together, so the difference never changed). A slow periodic
  // envelope is the one thing the ear refuses to habituate to; stationary
  // noise it stops hearing within seconds. So the tone is gone.
  //
  // Noise is also the right primitive on its own terms: water is broadband.
  // A sustained sine reads as machinery, which is exactly the word the bug
  // report used.
  const rumbleSrc = ctx.createBufferSource()
  rumbleSrc.buffer = noiseBuffer(ctx)
  rumbleSrc.loop = true
  const rumbleFilter = ctx.createBiquadFilter()
  rumbleFilter.type = 'lowpass'
  rumbleFilter.frequency.value = 110
  rumbleFilter.Q.value = 0.5
  const rumbleGain = ctx.createGain()
  rumbleGain.gain.value = 0
  rumbleSrc.connect(rumbleFilter)
  rumbleFilter.connect(rumbleGain)
  rumbleGain.connect(bus)
  rumbleSrc.start()

  // ── Wash: lowpassed noise water texture ──────────────────────────────────
  const washSrc = ctx.createBufferSource()
  washSrc.buffer = noiseBuffer(ctx)
  washSrc.loop = true
  const washFilter = ctx.createBiquadFilter()
  washFilter.type = 'lowpass'
  washFilter.frequency.value = 300
  washFilter.Q.value = 0.4
  const washGain = ctx.createGain()
  washGain.gain.value = 0
  washSrc.connect(washFilter)
  washFilter.connect(washGain)
  washGain.connect(bus)
  // Both bed layers read the same shared noise loop, so they start at
  // different points in it. Playing the identical sample stream through two
  // filters would make them one correlated source — the deep layer would
  // simply be the wash with its top taken off, and any repetition the loop
  // has would land in both at once instead of averaging out.
  washSrc.start(ctx.currentTime, NOISE_PHASE_OFFSET_S)

  // (Calm/tension pads retired 2026-07-02 — sourced zone music carries the
  //  musical layer now; tension ducks the music instead. See engine/music.ts.)

  // ── Water rush: bandpassed noise, swells with current proximity ──────────
  const rushSrc = ctx.createBufferSource()
  rushSrc.buffer = noiseBuffer(ctx)
  rushSrc.loop = true
  const rushFilter = ctx.createBiquadFilter()
  rushFilter.type = 'bandpass'
  rushFilter.frequency.value = 650
  rushFilter.Q.value = 0.8
  const rushGain = ctx.createGain()
  rushGain.gain.value = 0
  rushSrc.connect(rushFilter)
  rushFilter.connect(rushGain)
  rushGain.connect(bus)
  rushSrc.start()

  // ── Heartbeat bus (thumps are scheduled one pair at a time in steer) ─────
  const heartGain = ctx.createGain()
  heartGain.gain.value = 1
  heartGain.connect(bus)

  nodes = {
    ctx, rumbleFilter, rumbleGain,
    washFilter, washGain, rushGain, heartGain,
  }
  nextHeartbeat = 0
}

function thump(ctx: AudioContext, out: GainNode, at: number, vol: number): void {
  const osc = ctx.createOscillator()
  const g = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(70, at)
  osc.frequency.exponentialRampToValueAtTime(38, at + 0.12)
  g.gain.setValueAtTime(0, at)
  g.gain.linearRampToValueAtTime(vol, at + 0.015)
  g.gain.exponentialRampToValueAtTime(0.001, at + 0.22)
  osc.connect(g)
  g.connect(out)
  osc.start(at)
  osc.stop(at + 0.24)
}

/** Per-frame steering. Cheap no-op until initAmbientAudio has resolved. */
export function steerAmbient(s: AmbientScalars): void {
  if (!nodes) return
  const n = nodes
  const t = n.ctx.currentTime
  const A = T.audio.ambient

  // Both bed layers darken with depth. Nothing about them moves otherwise:
  // stationary is the point. The ear stops hearing unchanging noise within
  // seconds, and every attempt to keep a bed "alive" with a periodic wobble
  // buys attention back at exactly the moment you wanted it spent elsewhere.
  // If the bed ever needs life, it wants sporadic one-shots (a distant knock,
  // a bubble run) at irregular intervals — not modulation of the bed itself.
  n.rumbleGain.gain.setTargetAtTime(A.rumbleLevel, t, TAU)
  n.washGain.gain.setTargetAtTime(A.washLevel, t, TAU)
  n.rumbleFilter.frequency.setTargetAtTime(110 - 40 * s.biomeFactor, t, TAU)
  n.washFilter.frequency.setTargetAtTime(300 - 160 * s.biomeFactor, t, TAU)

  // Water rush.
  const rush = Math.max(0, Math.min(1, s.currentProximity))
  n.rushGain.gain.setTargetAtTime(A.rushLevel * rush * rush, t, 0.15)

  // Heartbeat: schedule lub-dub pairs while below the health threshold.
  // Severity 0 (at threshold) → 1 (empty) speeds the pulse and lifts volume.
  if (s.hpFrac <= A.heartbeatThreshold && s.hpFrac > 0) {
    if (t >= nextHeartbeat) {
      const severity = 1 - s.hpFrac / A.heartbeatThreshold
      const vol = A.heartbeatLevel * (0.5 + 0.5 * severity)
      thump(n.ctx, n.heartGain, t, vol)
      thump(n.ctx, n.heartGain, t + 0.28, vol * 0.7)
      const interval = 1.35 - 0.6 * severity
      nextHeartbeat = t + interval
    }
  } else {
    nextHeartbeat = 0 // re-arm immediately on next crossing
  }
}
